import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CollectionsIcon from '@mui/icons-material/Collections';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import { Action, ActionCreate, ActionUpdate, ActionDefaults, ACTION_VALIDATION, SEND_ACTION_DEFAULTS } from '../types/action';
import { actionsService } from '../services/actionsService';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { useTranslation } from 'react-i18next';
import { GallerySearchDialog } from './GallerySearchDialog';
import { AIGenerateDialog } from './AIGenerateDialog';

type PictureSource = 'upload' | 'camera' | 'gallery' | 'ai';

interface ActionEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  deviceId: string;
  action?: Action | null; // If provided, edit mode; otherwise create mode
}

export const ActionEditor: React.FC<ActionEditorProps> = ({
  open,
  onClose,
  onSave,
  deviceId,
  action,
}) => {
  const { t } = useTranslation();
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [soundFile, setSoundFile] = useState<File | null>(null);
  const [existingPictureUrl, setExistingPictureUrl] = useState<string | null>(null);
  const [existingSoundUrl, setExistingSoundUrl] = useState<string | null>(null);
  const [pictureObjectUrl, setPictureObjectUrl] = useState<string | null>(null);
  const [soundObjectUrl, setSoundObjectUrl] = useState<string | null>(null);
  const [selectedPicturePreview, setSelectedPicturePreview] = useState<string | null>(null);

  // Validation errors
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [pictureError, setPictureError] = useState('');
  const [soundError, setSoundError] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState(''); // "creating", "uploading picture", "uploading sound"
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Picture source state
  const [pictureSource, setPictureSource] = useState<PictureSource>('upload');
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [aiGeneratedImageUrl, setAiGeneratedImageUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Default send settings
  const [showDefaults, setShowDefaults] = useState(false);
  const [defaultTimeToStart, setDefaultTimeToStart] = useState<number>(SEND_ACTION_DEFAULTS.timeToStart);
  const [defaultStayUntilParentApproves, setDefaultStayUntilParentApproves] = useState<boolean>(SEND_ACTION_DEFAULTS.stayUntilParentApproves);
  const [defaultStayUntilChecked, setDefaultStayUntilChecked] = useState<boolean>(SEND_ACTION_DEFAULTS.stayUntilChecked);
  const [defaultStayUntilFixedTime, setDefaultStayUntilFixedTime] = useState<boolean>(SEND_ACTION_DEFAULTS.stayUntilFixedTime);
  const [defaultDuration, setDefaultDuration] = useState<number>(SEND_ACTION_DEFAULTS.duration);

  // Concurrent edit detection
  const [localEditTimestamp, setLocalEditTimestamp] = useState<number | null>(null);
  const [concurrentEditWarning, setConcurrentEditWarning] = useState(false);

  // Auto-created action for AI generation (allows create mode to transition to edit mode)
  const [autoCreatedAction, setAutoCreatedAction] = useState<Action | null>(null);

  // Effective action is either the prop or the auto-created one
  const effectiveAction = action || autoCreatedAction;

  // Reset form when dialog opens/closes or action changes
  useEffect(() => {
    if (open) {
      if (action) {
        // Edit mode - populate fields
        setName(action.name);
        setDescription(action.description);
        setExistingPictureUrl(action.pictureUrl);
        setExistingSoundUrl(action.soundUrl);
        setPictureFile(null);
        setSoundFile(null);
        setLocalEditTimestamp(Date.now());
        // Defaults
        if (action.defaults) {
          setShowDefaults(true);
          setDefaultTimeToStart(action.defaults.timeToStart ?? SEND_ACTION_DEFAULTS.timeToStart);
          setDefaultStayUntilParentApproves(action.defaults.stayUntilParentApproves ?? SEND_ACTION_DEFAULTS.stayUntilParentApproves);
          setDefaultStayUntilChecked(action.defaults.stayUntilChecked ?? SEND_ACTION_DEFAULTS.stayUntilChecked);
          setDefaultStayUntilFixedTime(action.defaults.stayUntilFixedTime ?? SEND_ACTION_DEFAULTS.stayUntilFixedTime);
          setDefaultDuration(action.defaults.duration ?? SEND_ACTION_DEFAULTS.duration);
        } else {
          setShowDefaults(false);
          setDefaultTimeToStart(SEND_ACTION_DEFAULTS.timeToStart);
          setDefaultStayUntilParentApproves(SEND_ACTION_DEFAULTS.stayUntilParentApproves);
          setDefaultStayUntilChecked(SEND_ACTION_DEFAULTS.stayUntilChecked);
          setDefaultStayUntilFixedTime(SEND_ACTION_DEFAULTS.stayUntilFixedTime);
          setDefaultDuration(SEND_ACTION_DEFAULTS.duration);
        }
      } else {
        // Create mode - clear fields
        setName('');
        setDescription('');
        setExistingPictureUrl(null);
        setExistingSoundUrl(null);
        setPictureFile(null);
        setSoundFile(null);
        setLocalEditTimestamp(null);
        setAutoCreatedAction(null);
        setShowDefaults(false);
        setDefaultTimeToStart(SEND_ACTION_DEFAULTS.timeToStart);
        setDefaultStayUntilParentApproves(SEND_ACTION_DEFAULTS.stayUntilParentApproves);
        setDefaultStayUntilChecked(SEND_ACTION_DEFAULTS.stayUntilChecked);
        setDefaultStayUntilFixedTime(SEND_ACTION_DEFAULTS.stayUntilFixedTime);
        setDefaultDuration(SEND_ACTION_DEFAULTS.duration);
      }
      // Clear errors and reset picture source
      setNameError('');
      setDescriptionError('');
      setPictureError('');
      setSoundError('');
      setErrorMessage('');
      setSuccessMessage('');
      setConcurrentEditWarning(false);
      setUploadProgress(0);
      setUploadPhase('');
      setPictureSource('upload');
      setShowCamera(false);
      setAiGeneratedImageUrl(null);
      stopCamera();
    }
  }, [open, action]);

  // Firebase real-time listener for concurrent edit detection
  useEffect(() => {
    if (!effectiveAction || !open || !deviceId) return;

    const actionRef = ref(database, `devices/${deviceId}/actions/${effectiveAction.id}`);

    const unsubscribe = onValue(actionRef, (snapshot) => {
      const data = snapshot.val();
      if (data && localEditTimestamp) {
        const updatedAt = data.updatedAt;
        const updatedBy = data.updatedBy;

        // Check if action was updated after we opened the editor
        if (updatedAt > localEditTimestamp && updatedBy !== effectiveAction.updatedBy) {
          setConcurrentEditWarning(true);
        }
      }
    });

    return () => {
      off(actionRef);
    };
  }, [effectiveAction, open, deviceId, localEditTimestamp]);

  // Fetch existing media files from backend proxy
  useEffect(() => {
    let pictureUrl: string | null = null;
    let soundUrl: string | null = null;

    const fetchMedia = async () => {
      if (!effectiveAction || !open || !deviceId) return;

      try {
        const user = auth.currentUser;
        if (!user) {
          console.error('User not authenticated');
          return;
        }

        const token = await user.getIdToken();
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        // Fetch picture if exists
        if (existingPictureUrl) {
          const pictureResponse = await fetch(
            `${API_BASE}/api/devices/${deviceId}/actions/${effectiveAction.id}/picture`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );

          if (pictureResponse.ok) {
            const blob = await pictureResponse.blob();
            pictureUrl = URL.createObjectURL(blob);
            setPictureObjectUrl(pictureUrl);
          }
        }

        // Fetch sound if exists
        if (existingSoundUrl) {
          const soundResponse = await fetch(
            `${API_BASE}/api/devices/${deviceId}/actions/${effectiveAction.id}/sound`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );

          if (soundResponse.ok) {
            const blob = await soundResponse.blob();
            soundUrl = URL.createObjectURL(blob);
            setSoundObjectUrl(soundUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching media:', error);
      }
    };

    fetchMedia();

    // Cleanup function to revoke object URLs
    return () => {
      if (pictureUrl) {
        URL.revokeObjectURL(pictureUrl);
      }
      if (soundUrl) {
        URL.revokeObjectURL(soundUrl);
      }
    };
  }, [effectiveAction, open, deviceId, existingPictureUrl, existingSoundUrl]);

  // Client-side file validation
  const validatePictureFile = (file: File): string | null => {
    if (file.size > ACTION_VALIDATION.PICTURE_MAX_SIZE) {
      return t('actions.pictureTooLarge', { max: ACTION_VALIDATION.PICTURE_MAX_SIZE / 1024 / 1024 });
    }

    const validTypes: readonly string[] = ACTION_VALIDATION.PICTURE_FORMATS;
    if (!validTypes.includes(file.type)) {
      return t('actions.invalidPictureFormat', { formats: ACTION_VALIDATION.PICTURE_EXTENSIONS.join(', ') });
    }

    return null;
  };

  const validateSoundFile = (file: File): string | null => {
    if (file.size > ACTION_VALIDATION.SOUND_MAX_SIZE) {
      return t('actions.soundTooLarge', { max: ACTION_VALIDATION.SOUND_MAX_SIZE / 1024 / 1024 });
    }

    const validTypes: readonly string[] = ACTION_VALIDATION.SOUND_FORMATS;
    if (!validTypes.includes(file.type)) {
      return t('actions.invalidSoundFormat', { formats: ACTION_VALIDATION.SOUND_EXTENSIONS.join(', ') });
    }

    return null;
  };

  // File selection handlers
  const handlePictureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const error = validatePictureFile(file);
      if (error) {
        setPictureError(error);
        setPictureFile(null);
      } else {
        setPictureError('');
        setPictureFile(file);
      }
    }
  };

  const handleSoundChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const error = validateSoundFile(file);
      if (error) {
        setSoundError(error);
        setSoundFile(null);
      } else {
        setSoundError('');
        setSoundFile(file);
      }
    }
  };

  // Create preview URL when picture file changes
  useEffect(() => {
    if (pictureFile) {
      const url = URL.createObjectURL(pictureFile);
      setSelectedPicturePreview(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setSelectedPicturePreview(null);
    }
  }, [pictureFile]);

  // Remove selected files
  const handleRemovePicture = () => {
    setPictureFile(null);
    setPictureError('');
    setSelectedPicturePreview(null);
  };

  const handleRemoveSound = () => {
    setSoundFile(null);
    setSoundError('');
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      setCameraStream(stream);
      setShowCamera(true);
    } catch (err) {
      console.error('Failed to access camera:', err);
      setPictureError(t('actions.pictureSource.cameraError'));
    }
  };

  // Effect to attach stream to video element after it mounts
  useEffect(() => {
    if (showCamera && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch((err) => {
        console.error('Failed to play video:', err);
      });
    }
  }, [showCamera, cameraStream]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref not available');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Check if video is ready
    if (video.readyState < 2) {
      console.error('Video not ready yet, readyState:', video.readyState);
      setPictureError(t('actions.pictureSource.cameraNotReady'));
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Check if dimensions are valid
    if (canvas.width === 0 || canvas.height === 0) {
      console.error('Invalid video dimensions:', canvas.width, canvas.height);
      setPictureError(t('actions.pictureSource.cameraNotReady'));
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
        const error = validatePictureFile(file);
        if (error) {
          setPictureError(error);
        } else {
          setPictureFile(file);
          setPictureError('');
        }
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  // Handle picture source change
  const handlePictureSourceChange = (
    _: React.MouseEvent<HTMLElement>,
    newSource: PictureSource | null
  ) => {
    if (newSource === null) return;

    // Stop camera if switching away from camera
    if (pictureSource === 'camera' && newSource !== 'camera') {
      stopCamera();
    }

    setPictureSource(newSource);

    // Handle each source type
    if (newSource === 'camera') {
      startCamera();
    } else if (newSource === 'gallery') {
      setGalleryDialogOpen(true);
    } else if (newSource === 'ai') {
      setAiDialogOpen(true);
    }
  };

  // Handle gallery selection
  const handleGallerySelect = (imageUrl: string, imageFile?: File) => {
    if (imageFile) {
      setPictureFile(imageFile);
      setPictureError('');
    }
    setGalleryDialogOpen(false);
    setPictureSource('upload');
  };

  // Handle AI generation
  const handleAiGenerated = (imageUrl: string) => {
    // Store the AI image URL for preview (don't need pictureFile since backend already saved it)
    setAiGeneratedImageUrl(imageUrl);
    // Keep AI tab active (don't switch to upload)
    setAiDialogOpen(false);
  };

  // Create action for AI generation (called when user clicks Generate without saving first)
  const handleCreateActionForAI = async (): Promise<string> => {
    if (!name.trim()) {
      throw new Error(t('actions.nameRequired'));
    }

    const newAction: ActionCreate = {
      name: name.trim(),
      description: description.trim() || name.trim(), // Use name as fallback
      pictureUrl: null,
      soundUrl: null,
    };

    const createdAction = await actionsService.createAction(deviceId, newAction);
    setAutoCreatedAction(createdAction);
    setLocalEditTimestamp(Date.now()); // Start concurrent edit detection
    return createdAction.id;
  };

  // Form validation
  const validateForm = (): boolean => {
    let isValid = true;

    // Name validation
    if (!name.trim()) {
      setNameError(t('actions.nameRequired'));
      isValid = false;
    } else if (name.length > ACTION_VALIDATION.NAME_MAX_LENGTH) {
      setNameError(t('actions.nameTooLong', { max: ACTION_VALIDATION.NAME_MAX_LENGTH }));
      isValid = false;
    } else {
      setNameError('');
    }

    // Description validation
    if (!description.trim()) {
      setDescriptionError(t('actions.descriptionRequired'));
      isValid = false;
    } else if (description.length > ACTION_VALIDATION.DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(t('actions.descriptionTooLong', { max: ACTION_VALIDATION.DESCRIPTION_MAX_LENGTH }));
      isValid = false;
    } else {
      setDescriptionError('');
    }

    return isValid;
  };

  // Build defaults payload
  const buildDefaults = (): ActionDefaults | null => {
    if (!showDefaults) return null;
    return {
      timeToStart: defaultTimeToStart,
      stayUntilParentApproves: defaultStayUntilParentApproves,
      stayUntilChecked: defaultStayUntilChecked,
      stayUntilFixedTime: defaultStayUntilFixedTime,
      duration: defaultDuration,
    };
  };

  // Save handler
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      let actionId: string;

      if (effectiveAction) {
        // Edit mode (or auto-created action) - update existing action
        setUploadPhase(t('actions.updatingAction'));
        setUploadProgress(10);

        const updates: ActionUpdate = {
          name: name.trim(),
          description: description.trim(),
          defaults: buildDefaults(),
        };

        await actionsService.updateAction(deviceId, effectiveAction.id, updates);
        actionId = effectiveAction.id;
        setUploadProgress(30);
      } else {
        // Create mode - create new action
        setUploadPhase(t('actions.creatingAction'));
        setUploadProgress(10);

        const newAction: ActionCreate = {
          name: name.trim(),
          description: description.trim(),
          pictureUrl: null,
          soundUrl: null,
          defaults: buildDefaults(),
        };

        const createdAction = await actionsService.createAction(deviceId, newAction);
        actionId = createdAction.id;
        setUploadProgress(30);
      }

      // Upload picture if provided
      if (pictureFile) {
        setUploadPhase(t('actions.uploadingPicture'));
        setUploadProgress(40);
        await actionsService.uploadPicture(deviceId, actionId, pictureFile);
        setUploadProgress(65);
      } else if (effectiveAction && !pictureFile) {
        // If editing and no new picture, skip to next phase
        setUploadProgress(65);
      }

      // Upload sound if provided
      if (soundFile) {
        setUploadPhase(t('actions.uploadingSound'));
        setUploadProgress(70);
        await actionsService.uploadSound(deviceId, actionId, soundFile);
        setUploadProgress(95);
      } else if (effectiveAction && !soundFile) {
        // If editing and no new sound, skip to next phase
        setUploadProgress(95);
      }

      // Complete
      setUploadProgress(100);
      setUploadPhase('');
      setSuccessMessage(effectiveAction ? t('actions.actionUpdatedSuccess') : t('actions.actionCreatedSuccess'));

      // Wait a moment to show success message, then close
      setTimeout(() => {
        onSave();
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Failed to save action:', error);
      setErrorMessage(t('actions.failedToSaveAction'));
      setUploadProgress(0);
      setUploadPhase('');
    } finally {
      setLoading(false);
    }
  };

  // Cancel handler
  const handleCancel = () => {
    // Reset form state
    setName('');
    setDescription('');
    setPictureFile(null);
    setSoundFile(null);
    setExistingPictureUrl(null);
    setExistingSoundUrl(null);
    setNameError('');
    setDescriptionError('');
    setPictureError('');
    setSoundError('');
    setErrorMessage('');
    setSuccessMessage('');
    setConcurrentEditWarning(false);
    setUploadProgress(0);
    setUploadPhase('');

    onClose();
  };

  // Delete handlers
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!effectiveAction) return;

    setDeleting(true);
    setDeleteDialogOpen(false);

    try {
      await actionsService.deleteAction(deviceId, effectiveAction.id);
      setSuccessMessage(t('actions.actionDeletedSuccess'));

      // Wait a moment to show success message, then close
      setTimeout(() => {
        onSave(); // Trigger refresh in parent
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Failed to delete action:', error);
      setErrorMessage(t('actions.failedToDeleteAction'));
    } finally {
      setDeleting(false);
    }
  };

  const isFormValid = name.trim() && description.trim() && !nameError && !descriptionError && !pictureError && !soundError;

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {effectiveAction ? t('actions.editAction') : t('actions.createNewAction')}
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Concurrent edit warning */}
          {concurrentEditWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('actions.concurrentEditWarning')}
            </Alert>
          )}

          {/* Name field */}
          <TextField
            label={t('actions.actionName')}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!nameError}
            helperText={nameError || t('actions.charactersCount', { current: name.length, max: ACTION_VALIDATION.NAME_MAX_LENGTH })}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {/* Description field */}
          <TextField
            label={t('actions.description')}
            fullWidth
            required
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!descriptionError}
            helperText={descriptionError || t('actions.charactersCount', { current: description.length, max: ACTION_VALIDATION.DESCRIPTION_MAX_LENGTH })}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {/* Picture section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('actions.picture')}
            </Typography>

            {/* Existing picture preview */}
            {existingPictureUrl && !pictureFile && pictureObjectUrl && (
              <Box sx={{ mb: 1 }}>
                <img
                  src={pictureObjectUrl}
                  alt="Current picture"
                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  {t('actions.currentPicture')}
                </Typography>
              </Box>
            )}

            {/* Picture source selector */}
            <ToggleButtonGroup
              value={pictureSource}
              exclusive
              onChange={handlePictureSourceChange}
              fullWidth
              size="small"
              disabled={loading}
              sx={(theme) => ({
                mb: 1,
                // At phone width the four choices do not fit in one row: a 2×2 grid
                // of separate 44 px buttons instead of the joined group.
                [theme.breakpoints.only('xs')]: {
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1,
                  '& .MuiToggleButtonGroup-grouped, & .MuiToggleButtonGroup-grouped.Mui-disabled': {
                    minHeight: 44,
                    m: 0,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  },
                },
              })}
            >
              <ToggleButton value="upload">
                <CloudUploadIcon sx={{ mr: 0.5 }} />
                {t('actions.pictureSource.upload')}
              </ToggleButton>
              <ToggleButton value="camera">
                <CameraAltIcon sx={{ mr: 0.5 }} />
                {t('actions.pictureSource.camera')}
              </ToggleButton>
              <ToggleButton value="gallery">
                <CollectionsIcon sx={{ mr: 0.5 }} />
                {t('actions.pictureSource.gallery')}
              </ToggleButton>
              <ToggleButton value="ai">
                <AutoAwesomeIcon sx={{ mr: 0.5 }} />
                {t('actions.pictureSource.ai')}
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Upload mode */}
            {pictureSource === 'upload' && !showCamera && (
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={loading}
                fullWidth
              >
                {pictureFile ? t('actions.changePicture') : existingPictureUrl ? t('actions.replacePicture') : t('actions.uploadPicture')}
                <input
                  type="file"
                  hidden
                  accept={ACTION_VALIDATION.PICTURE_EXTENSIONS.join(',')}
                  onChange={handlePictureChange}
                />
              </Button>
            )}

            {/* Camera mode */}
            {showCamera && (
              <Box sx={{ position: 'relative' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: 8 }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={capturePhoto}
                    startIcon={<CameraAltIcon />}
                  >
                    {t('actions.pictureSource.capture')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={stopCamera}
                  >
                    {t('common.cancel')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* AI mode - show generated image preview and generate button */}
            {pictureSource === 'ai' && (
              <>
                {/* AI generated image preview */}
                {aiGeneratedImageUrl && (
                  <Box sx={{ position: 'relative', display: 'inline-block', mt: 1 }}>
                    <img
                      src={aiGeneratedImageUrl}
                      alt={t('actions.ai.generatedImage')}
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        borderRadius: 8,
                        border: '1px solid #ddd',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAiGeneratedImageUrl(null);
                      }}
                      disabled={loading}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                        width: 24,
                        height: 24,
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('actions.ai.generatedImage')}
                    </Typography>
                  </Box>
                )}
                {/* Button to open AI dialog */}
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setAiDialogOpen(true)}
                  disabled={loading}
                  fullWidth
                  sx={{ mt: aiGeneratedImageUrl ? 1 : 0 }}
                >
                  {aiGeneratedImageUrl ? t('actions.ai.regenerate') : t('actions.ai.generate')}
                </Button>
              </>
            )}

            {/* Selected picture preview */}
            {pictureFile && selectedPicturePreview && (
              <Box sx={{ position: 'relative', display: 'inline-block', mt: 1 }}>
                <img
                  src={selectedPicturePreview}
                  alt="Selected picture"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemovePicture}
                  disabled={loading}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                    width: 24,
                    height: 24,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('actions.newPicture')}
                </Typography>
              </Box>
            )}

            {/* Picture error */}
            {pictureError && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                {pictureError}
              </Typography>
            )}
          </Box>

          {/* Sound upload */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('actions.sound')}
            </Typography>
            {existingSoundUrl && !soundFile && soundObjectUrl && (
              <Box sx={{ mb: 1 }}>
                <audio controls src={soundObjectUrl} style={{ width: '100%' }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  {t('actions.currentSound')}
                </Typography>
              </Box>
            )}
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              disabled={loading}
              fullWidth
            >
              {soundFile ? t('actions.changeSound') : existingSoundUrl ? t('actions.replaceSound') : t('actions.uploadSound')}
              <input
                type="file"
                hidden
                accept={ACTION_VALIDATION.SOUND_EXTENSIONS.join(',')}
                onChange={handleSoundChange}
              />
            </Button>
            {soundFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {t('actions.fileSizeDisplay', { name: soundFile.name, size: (soundFile.size / 1024 / 1024).toFixed(2) })}
                </Typography>
                <IconButton size="small" onClick={handleRemoveSound} disabled={loading}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            {soundError && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                {soundError}
              </Typography>
            )}
          </Box>

          {/* Send Defaults Section */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showDefaults}
                  onChange={(e) => setShowDefaults(e.target.checked)}
                  disabled={loading}
                />
              }
              label={
                <Typography variant="subtitle2">
                  {t('actions.defaults.customizeDefaults')}
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
              {t('actions.defaults.customizeDefaultsHelper')}
            </Typography>

            {showDefaults && (
              <Box sx={{ ml: 2, mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                <TextField
                  label={t('sendAction.timeToStart')}
                  type="number"
                  value={defaultTimeToStart}
                  onChange={(e) => setDefaultTimeToStart(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  fullWidth
                  margin="dense"
                  disabled={loading}
                  inputProps={{ min: 0 }}
                  size="small"
                />

                <Typography variant="subtitle2" sx={{ mt: 1 }} gutterBottom>
                  {t('sendAction.actionCompletesWhen')}
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={defaultStayUntilParentApproves}
                      onChange={(e) => setDefaultStayUntilParentApproves(e.target.checked)}
                      disabled={loading}
                      size="small"
                    />
                  }
                  label={t('sendAction.stayUntilParentApproves')}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={defaultStayUntilChecked}
                      onChange={(e) => setDefaultStayUntilChecked(e.target.checked)}
                      disabled={loading}
                      size="small"
                    />
                  }
                  label={t('sendAction.stayUntilChecked')}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={defaultStayUntilFixedTime}
                      onChange={(e) => setDefaultStayUntilFixedTime(e.target.checked)}
                      disabled={loading}
                      size="small"
                    />
                  }
                  label={t('sendAction.stayUntilFixedTime')}
                />

                {defaultStayUntilFixedTime && (
                  <TextField
                    label={t('sendAction.durationMinutes')}
                    type="number"
                    value={defaultDuration}
                    onChange={(e) => setDefaultDuration(Math.max(1, Math.min(1440, parseInt(e.target.value, 10) || 1)))}
                    margin="dense"
                    disabled={loading}
                    inputProps={{ min: 1, max: 1440 }}
                    size="small"
                    // Indented under its checkbox, so it is narrower by the indent: fullWidth alone
                    // would push it 32 px past the defaults box's right edge (BA-110)
                    sx={{ ml: 4, width: (theme) => `calc(100% - ${theme.spacing(4)})` }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Upload progress */}
          {loading && uploadProgress > 0 && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {uploadPhase}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {/* Delete button - only show in edit mode */}
          {effectiveAction ? (
            <Button
              onClick={handleDeleteClick}
              color="error"
              disabled={loading || deleting}
              startIcon={<DeleteIcon />}
            >
              {t('common.delete')}
            </Button>
          ) : (
            <Box sx={{ flex: 0 }} />
          )}

          {/* Right side buttons */}
          <Box>
            <Button onClick={handleCancel} disabled={loading || deleting} sx={{ mr: 1 }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!isFormValid || loading || deleting}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? t('common.saving') : effectiveAction ? t('common.save').replace('Save', 'Update') : t('common.save').replace('Save', 'Create')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('actions.deleteAction')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('actions.deleteActionConfirm', { name: effectiveAction?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            autoFocus
          >
            {deleting ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gallery Search Dialog */}
      <GallerySearchDialog
        open={galleryDialogOpen}
        onClose={() => {
          setGalleryDialogOpen(false);
          setPictureSource('upload');
        }}
        onSelect={handleGallerySelect}
      />

      {/* AI Generate Dialog */}
      <AIGenerateDialog
        open={aiDialogOpen}
        onClose={() => {
          setAiDialogOpen(false);
          // Keep AI tab active (don't switch to upload)
        }}
        onGenerated={handleAiGenerated}
        deviceId={deviceId}
        actionId={effectiveAction?.id}
        actionName={name}
        actionDescription={description}
        onCreateAction={handleCreateActionForAI}
      />
    </>
  );
};
